import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  TableRow,
  TableCell,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SettingsIcon from "@mui/icons-material/Settings";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import EditIcon from "@mui/icons-material/Edit";
import {
  toggleServiceScheduler,
  toggleServiceAutoApproval,
  toggleServiceEmailAdmin,
  toggleServiceEmailAssociate,
  toggleServiceEnabled,
} from "../../../actions/servicesActions";
import UpdateAdminEmailsDialog from "./UpdateAdminEmailsDialog";
import SchedulerNextRunIndicator from "../../../components/SchedulerNextRunIndicator";

const ServicesListItem = ({ project, onRefresh }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const service = project.service;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  const [serviceEnabled, setServiceEnabled] = useState(
    service?.enabled || false
  );
  const [schedulerEnabled, setSchedulerEnabled] = useState(
    service?.scheduler?.enabled || false
  );
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(
    service?.autoApproval?.enabled || false
  );
  const [emailAdminEnabled, setEmailAdminEnabled] = useState(
    service?.notifications?.notifyAdminOnCompletion || false
  );
  const [emailAssociateEnabled, setEmailAssociateEnabled] = useState(
    service?.notifications?.notifyAssociateOnApproval || false
  );

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewProject = () => {
    handleMenuClose();
    navigate(`/dashboard/proyectos/${project._id}`);
  };

  const handleViewService = () => {
    handleMenuClose();
    navigate(`/dashboard/proyectos/${project._id}/servicio`);
  };

  const handleToggleEnabled = () => {
    handleMenuClose();
    if (!service?._id) return;
    const newValue = !serviceEnabled;
    dispatch(
      toggleServiceEnabled({
        projectId: project._id,
        serviceId: service._id,
        enabled: newValue,
        onSuccess: () => {
          setServiceEnabled(newValue);
          if (onRefresh) onRefresh();
        },
        onFail: () => {},
      })
    );
  };

  const handleToggleScheduler = () => {
    handleMenuClose();
    if (!service?._id) return;
    const newValue = !schedulerEnabled;
    dispatch(
      toggleServiceScheduler({
        projectId: project._id,
        serviceId: service._id,
        enabled: newValue,
        onSuccess: () => {
          setSchedulerEnabled(newValue);
          if (onRefresh) onRefresh();
        },
        onFail: () => {},
      })
    );
  };

  const handleToggleAutoApproval = () => {
    handleMenuClose();
    if (!service?._id) return;
    const newValue = !autoApprovalEnabled;
    dispatch(
      toggleServiceAutoApproval({
        projectId: project._id,
        serviceId: service._id,
        enabled: newValue,
        onSuccess: () => {
          setAutoApprovalEnabled(newValue);
          if (onRefresh) onRefresh();
        },
        onFail: () => {},
      })
    );
  };

  const handleToggleEmailAdmin = () => {
    handleMenuClose();
    if (!service?._id) return;
    const newValue = !emailAdminEnabled;
    dispatch(
      toggleServiceEmailAdmin({
        projectId: project._id,
        serviceId: service._id,
        enabled: newValue,
        onSuccess: () => {
          setEmailAdminEnabled(newValue);
          if (onRefresh) onRefresh();
        },
        onFail: () => {},
      })
    );
  };

  const handleToggleEmailAssociate = () => {
    handleMenuClose();
    if (!service?._id) return;
    const newValue = !emailAssociateEnabled;
    dispatch(
      toggleServiceEmailAssociate({
        projectId: project._id,
        serviceId: service._id,
        enabled: newValue,
        onSuccess: () => {
          setEmailAssociateEnabled(newValue);
          if (onRefresh) onRefresh();
        },
        onFail: () => {},
      })
    );
  };

  const handleOpenEmailDialog = () => {
    handleMenuClose();
    setShowEmailDialog(true);
  };

  const handleCloseEmailDialog = () => {
    setShowEmailDialog(false);
  };

  const handleEmailDialogSuccess = () => {
    if (onRefresh) onRefresh();
  };

  const StatusChip = ({ enabled }) => (
    <Chip
      label={enabled ? "Activo" : "Inactivo"}
      color={enabled ? "success" : "default"}
      size="small"
      sx={{ minWidth: 70 }}
    />
  );

  if (!service) {
    return (
      <TableRow>
        <TableCell>{project.name}</TableCell>
        <TableCell colSpan={6}>
          <Chip label="Sin servicio configurado" color="warning" size="small" />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <Typography fontWeight="medium">{project.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {project.alias || ""}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
          {service.name}
        </Typography>
        <Chip
          label={serviceEnabled ? "Activo" : "Inactivo"}
          color={serviceEnabled ? "info" : "default"}
          size="small"
          sx={{ mt: 0.5 }}
        />
      </TableCell>
      <TableCell align="center">
        <SchedulerNextRunIndicator scheduler={service?.scheduler} />
      </TableCell>
      <TableCell align="center">
        <StatusChip enabled={autoApprovalEnabled} />
      </TableCell>
      <TableCell align="center">
        <StatusChip enabled={emailAdminEnabled} />
      </TableCell>
      {/* <TableCell align="center">
        <StatusChip enabled={emailAssociateEnabled} />
      </TableCell> */}
      <TableCell align="center">
        <IconButton onClick={handleMenuOpen}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleViewProject}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver proyecto</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleViewService}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver servicio</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleToggleEnabled}>
            <ListItemIcon>
              <PowerSettingsNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {serviceEnabled ? "Desactivar" : "Activar"} servicio
            </ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleToggleScheduler}>
            <ListItemIcon>
              <ScheduleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {schedulerEnabled ? "Desactivar" : "Activar"} scheduler
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleToggleAutoApproval}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {autoApprovalEnabled ? "Desactivar" : "Activar"} auto aprobación
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleToggleEmailAdmin}>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {emailAdminEnabled ? "Desactivar" : "Activar"} email para admin
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleToggleEmailAssociate} disabled>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {emailAssociateEnabled ? "Desactivar" : "Activar"} email asociado
            </ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleOpenEmailDialog}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar emails admin</ListItemText>
          </MenuItem>
        </Menu>
      </TableCell>
      <UpdateAdminEmailsDialog
        open={showEmailDialog}
        onClose={handleCloseEmailDialog}
        project={project}
        service={service}
        onSuccess={handleEmailDialogSuccess}
      />
    </TableRow>
  );
};

export default ServicesListItem;
